import {
  Card,
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Stack,
  CardMedia,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useState, MouseEvent } from 'react';
import {
  StreamingApp,
  createApp,
  updateApp,
  deleteApp,
} from '../../../../state/Apps';
import ActiveSessionsDialog from './ActiveSessionsDialog';

interface ApplicationCardProps {
  app: StreamingApp;
  onEdit?: (app: StreamingApp) => void;
  onDelete?: (app: StreamingApp) => void;
  onCreateNew?: () => void;
  solutionId?: string;
  siteId?: string;
  twinId?: string;
}

export default function ApplicationCard({
  app,
  onEdit,
  onDelete,
  onCreateNew,
  solutionId,
  siteId,
  twinId,
}: ApplicationCardProps) {
  const [version, setVersion] = useState(app.latestVersion);
  const [versionAnchorEl, setVersionAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSessionsDialog, setOpenSessionsDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isVersionMenuOpen = Boolean(versionAnchorEl);
  const isActionMenuOpen = Boolean(actionAnchorEl);

  const handleVersionMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setVersionAnchorEl(event.currentTarget);
  };

  const handleActionMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setActionAnchorEl(event.currentTarget);
  };

  const handleVersionMenuClose = () => {
    setVersionAnchorEl(null);
  };

  const handleActionMenuClose = () => {
    setActionAnchorEl(null);
  };

  const handleVersionSelect = (
    selectedVersion: typeof version,
    event: React.MouseEvent<HTMLLIElement>
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setVersion(selectedVersion);
    handleVersionMenuClose();
  };

  const handleEditClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onEdit) {
      onEdit(app);
    } else {
      setOpenEditDialog(true);
    }
    handleActionMenuClose();
  };

  const handleDeleteClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onDelete) {
      onDelete(app);
    } else {
      setOpenDeleteDialog(true);
    }
    handleActionMenuClose();
  };

  const confirmEdit = async (updatedApp: Partial<StreamingApp>) => {
    if (!app.id) return;

    setIsLoading(true);
    try {
      await updateApp(app.id, updatedApp);
      console.log('App updated successfully');

      setOpenEditDialog(false);
    } catch (error) {
      console.error('Failed to update app:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!app.id) return;

    setIsLoading(true);
    try {
      await deleteApp(app.id);
      console.log('App deleted successfully');

      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete app:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardClick = (event: MouseEvent) => {
    event.preventDefault();
    
    console.log('=== ApplicationCard handleCardClick ===');
    console.log('Props received:', { solutionId, siteId, twinId, appId: app.id });
    
    // Navigate to sessions page with required params
    const params = new URLSearchParams({
      solutionId: solutionId || '',
      siteId: siteId || '',
      twinId: twinId || '',
    });
    
    const url = `/app/${app.id}/sessions?${params.toString()}`;
    console.log('Opening URL:', url);
    
    window.open(url, '_blank');
  };

  return (
    <Card
      component="a"
      href="#"
      onClick={handleCardClick}
      variant="outlined"
      sx={{
        display: 'block',
        textDecoration: 'none',
        borderRadius: 1,
        cursor: 'pointer',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            p: 1,
            flex: 1,
            overflow: 'hidden',
            alignItems: 'flex-end',
          }}
        >
          <CardMedia
            component="img"
            sx={{ width: 80, height: 80 }}
            image={app.icon}
            title={app.title}
          />
          <Stack spacing={0.3} sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="subtitle2">{app.productArea}</Typography>
            <Typography
              variant="h6"
              sx={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                overflow: 'hidden',
                lineHeight: '30px',
                fontSize: '20px',
              }}
              title={app.title}
            >
              {app.title}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
              sx={{ alignSelf: 'flex-end' }}
            >
              {version.name}
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Version">
            <IconButton
              size="small"
              sx={{
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  borderRadius: '50%',
                },
              }}
              onClick={handleVersionMenuClick}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit">
            <IconButton
              size="small"
              sx={{
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  borderRadius: '50%',
                },
              }}
              onClick={handleEditClick}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton
              size="small"
              sx={{
                borderRadius: '50%',
                width: 32,
                height: 32,
                '&:hover': {
                  borderRadius: '50%',
                },
              }}
              onClick={handleDeleteClick}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={versionAnchorEl}
            open={isVersionMenuOpen}
            onClose={handleVersionMenuClose}
            onClick={(e) => e.stopPropagation()}
          >
            {app.versions.map((version) => (
              <MenuItem
                key={version.id}
                onClick={(event) => handleVersionSelect(version, event)}
              >
                {version.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Application</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{app.title}"? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Application</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Edit the application details below
          </DialogContentText>

          <Box component="form" noValidate sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="title"
              label="Application Title"
              name="title"
              defaultValue={app.title}
              autoFocus
            />
            <TextField
              margin="normal"
              fullWidth
              id="description"
              label="Description"
              name="description"
              defaultValue={app.description || ''}
              multiline
              rows={3}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="productArea"
              label="Product Area"
              name="productArea"
              defaultValue={app.productArea}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                id="status"
                defaultValue={app.status || 'active'}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              const updatedApp = {
                title:
                  (document.getElementById('title') as HTMLInputElement)
                    ?.value || app.title,
                description:
                  (document.getElementById('description') as HTMLInputElement)
                    ?.value || app.description,
                productArea:
                  (document.getElementById('productArea') as HTMLInputElement)
                    ?.value || app.productArea,
                status:
                  ((document.getElementById('status') as HTMLSelectElement)
                    ?.value as any) || app.status,
              };
              confirmEdit(updatedApp);
            }}
            color="primary"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <ActiveSessionsDialog
        open={openSessionsDialog}
        onClose={() => setOpenSessionsDialog(false)}
        app={app}
        appName={app.title}
        solutionId={solutionId}
        siteId={siteId}
        twinId={twinId}
      />
    </Card>
  );
}
